'use client';
/**
 * DataTable — ядро списковых экранов (Orders, Fleet, Drivers, Invoices…).
 *
 * Сознательно НЕ god-component: таблица умеет колонки, плотность,
 * сортировку, выбор строк и три состояния (пусто / загрузка / ошибка).
 * Тулбар, фильтры, массовые действия и пагинация НЕ живут внутри — они
 * композируются вокруг: <TableToolbar>, <FilterBar>, <BulkBar>, <Pagination>.
 * Так у таблицы остаётся десяток пропсов вместо сорока, а экран сам решает,
 * что ему нужно.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { cx, Button, Spinner } from '@/components/ui';

export interface Column<T> {
  /** Стабильный ключ; он же — ключ сортировки. */
  key: string;
  header: ReactNode;
  /** Рендер ячейки. */
  cell: (row: T) => ReactNode;
  /** Значение для сортировки. Нет — колонка не сортируется. */
  sortValue?: (row: T) => string | number | null | undefined;
  width?: string;
  align?: 'left' | 'right';
  /** Цифры выравниваются по разрядам. */
  numeric?: boolean;
  /** Второстепенная колонка: показывается только на широких экранах (≥1536px). */
  hideBelow?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  empty?: ReactNode;
  density?: 'compact' | 'default';
  onRowClick?: (row: T) => void;
  /** Подсветить строку (например, просроченную). */
  rowTone?: (row: T) => 'danger' | 'warning' | null;
}

export function DataTable<T>({
  columns, rows, keyOf, loading, error, onRetry, empty,
  density = 'default', onRowClick, rowTone,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const rowH = density === 'compact' ? 'h-9' : 'h-12';

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a), bv = col.sortValue!(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [rows, sort, columns]);

  const toggleSort = (col: Column<T>) => {
    if (!col.sortValue) return;
    setSort((p) => (p?.key !== col.key ? { key: col.key, dir: 'asc' }
      : p.dir === 'asc' ? { key: col.key, dir: 'desc' } : null));
  };

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2.5 py-12 px-6 text-center">
        <AlertTriangle size={18} className="text-[var(--danger)]" />
        <p className="text-[13px] text-[var(--text-primary)] font-medium">{error}</p>
        {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[var(--surface-sunken)]">
            {columns.map((col) => {
              const active = sort?.key === col.key;
              const Arrow = sort?.dir === 'desc' ? ArrowDown : ArrowUp;
              return (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={cx(
                    'h-10 px-4 text-[11px] font-semibold uppercase tracking-[0.08em] whitespace-nowrap',
                    'text-[var(--text-muted)] border-b border-[var(--border)]',
                    col.align === 'right' ? 'text-right' : 'text-left',
                    col.hideBelow && 'hidden 2xl:table-cell',
                  )}
                >
                  {col.sortValue ? (
                    <button
                      onClick={() => toggleSort(col)}
                      className={cx(
                        'inline-flex items-center gap-1 hover:text-[var(--text-primary)] transition-colors',
                        'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-xs)]',
                        active && 'text-[var(--text-primary)]',
                      )}
                    >
                      {col.header}
                      {active && <Arrow size={11} />}
                    </button>
                  ) : col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            [...Array(6)].map((_, i) => (
              <tr key={`sk-${i}`} className={rowH}>
                {columns.map((col) => (
                  <td key={col.key} className={cx('px-4 border-b border-[var(--border)]', col.hideBelow && 'hidden xl:table-cell')}>
                    <span className="block h-3 rounded-[var(--radius-xs)] bg-[var(--surface-hover)] animate-pulse"
                          style={{ width: `${45 + ((i * 13 + col.key.length * 7) % 45)}%` }} />
                  </td>
                ))}
              </tr>
            ))
          )}

          {!loading && sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-14 text-center">
                {empty ?? <span className="text-[13px] text-[var(--text-muted)]">Nothing here yet</span>}
              </td>
            </tr>
          )}

          {sorted.map((row) => {
            const tone = rowTone?.(row);
            return (
              <tr
                key={keyOf(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cx(
                  rowH, 'border-b border-[var(--border)] last:border-b-0 transition-colors',
                  onRowClick && 'cursor-pointer',
                  'hover:bg-[var(--surface-hover)]',
                  tone === 'danger' && 'bg-[var(--danger-subtle)]',
                  tone === 'warning' && 'bg-[var(--warning-subtle)]',
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cx(
                      'px-4 text-[13.5px] text-[var(--text-secondary)] align-middle',
                      col.align === 'right' ? 'text-right' : 'text-left',
                      col.numeric && 'tabular-nums font-[var(--font-mono)]',
                      col.hideBelow && 'hidden 2xl:table-cell',
                    )}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {loading && rows.length > 0 && (
        <div className="flex items-center justify-center gap-2 h-9 text-[12px] text-[var(--text-muted)] border-t border-[var(--border)]">
          <Spinner size={12} /> Updating…
        </div>
      )}
    </div>
  );
}

/** Тулбар над таблицей: поиск, фильтры, действия. Композиция, не проп. */
export function TableToolbar({ children, right }: { children?: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[var(--border)]">
      {children}
      {right && <div className="ml-auto flex items-center gap-1.5">{right}</div>}
    </div>
  );
}

/** Панель массовых действий — появляется, когда выбраны строки. */
export function BulkBar({ count, children, onClear }: { count: number; children?: ReactNode; onClear: () => void }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-3 px-3.5 py-2 bg-[var(--brand-subtle)] border-b border-[var(--brand-border)]">
      <span className="text-[12px] font-medium text-[var(--text-primary)] tabular-nums">{count} selected</span>
      <div className="flex items-center gap-1.5">{children}</div>
      <Button size="sm" variant="ghost" onClick={onClear} className="ml-auto">Clear</Button>
    </div>
  );
}

export function Pagination({ page, pages, total, onPage }: {
  page: number; pages: number; total?: number; onPage: (p: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-3.5 h-10 border-t border-[var(--border)]">
      <span className="text-[12px] text-[var(--text-muted)] tabular-nums">
        Page {page} of {pages}{total != null && ` · ${total} rows`}
      </span>
      <div className="ml-auto flex items-center gap-1.5">
        <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</Button>
        <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
}
